import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Calendar, ArrowLeft, Tag, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`).then((res) => {
      setPost(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#0F172A] min-h-screen">
        <Navbar />
        <div className="pt-28 text-center text-slate-400 py-20">Loading...</div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-[#0F172A] min-h-screen">
        <Navbar />
        <div className="pt-28 text-center py-20">
          <h2 className="text-white text-2xl font-bold mb-4">Post Not Found</h2>
          <Link to="/blog" className="text-orange-400 hover:text-orange-300">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0F172A] min-h-screen">
      <Helmet>
        <title>{`${post.title} — VSINDLPARK.COM`}</title>
        <meta name="description" content={post.excerpt || post.title} />
        <link rel="canonical" href={`https://vsindlpark.in/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        {post.image_url && <meta property="og:image" content={post.image_url} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt || post.title,
          ...(post.image_url ? { image: post.image_url } : {}),
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
          articleSection: post.category,
          author: { "@type": "Organization", name: "VS Agro Infra Enterprises" },
          publisher: {
            "@type": "Organization",
            name: "VS Agro Infra Enterprises",
            logo: { "@type": "ImageObject", url: "https://vsindlpark.in/logo192.png" },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `https://vsindlpark.in/blog/${post.slug}` },
        })}</script>
      </Helmet>
      <Navbar />

      <div className="bg-slate-900 pt-28 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-slate-400 hover:text-orange-400 text-sm mb-4 transition-colors" data-testid="blog-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2 py-0.5 text-xs font-bold border border-orange-500/30 bg-orange-500/20 text-orange-400 capitalize">
              <Tag className="inline w-3 h-3 mr-1" />{post.category}
            </span>
            <span className="text-slate-500 text-xs flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {post.created_at?.slice(0, 10)}
            </span>
          </div>
          <h1 className="text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", textTransform: "uppercase" }}>
            {post.title}
          </h1>
        </div>
      </div>

      <div className="bg-[#0F172A] py-12" data-testid="blog-post-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.image_url && (
            <div className="mb-8 border border-white/10 overflow-hidden">
              <img src={post.image_url} alt={post.title} className="w-full max-h-96 object-cover" />
            </div>
          )}
          <div className="prose prose-invert prose-orange max-w-none">
            <div className="text-slate-300 text-base leading-relaxed whitespace-pre-wrap" style={{ lineHeight: 1.8 }}>
              {post.content}
            </div>
          </div>

          {/* Share */}
          <div className="mt-10 pt-6 border-t border-white/10 flex items-center gap-4">
            <span className="text-slate-500 text-sm flex items-center gap-1"><Share2 className="w-4 h-4" /> Share:</span>
            <a href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - VSINDLPARK.COM')}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-emerald-400 text-sm transition-colors">WhatsApp</a>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400 text-sm transition-colors">Twitter</a>
          </div>

          {/* CTA */}
          <div className="mt-10 bg-orange-500/10 border border-orange-500/30 p-6 text-center">
            <h3 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: "uppercase" }}>
              Interested in Industrial Plots?
            </h3>
            <p className="text-slate-400 text-sm mb-4">Explore available plots at IIE Sigaddi, SIIDCUL Kotdwar</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/plots" className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-colors">View Plots</Link>
              <Link to="/booking" className="border border-white/20 text-white hover:border-orange-500 px-6 py-2.5 font-bold uppercase tracking-wider text-sm transition-colors">Book Now</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
